// scorm-again's /scorm2004 types declare a `default` export but the ESM runtime
// uses a NAMED one — type-only default import for types, namespace for the ctor.
import type Scorm2004API from 'scorm-again/scorm2004';
import * as Scorm2004Ns from 'scorm-again/scorm2004';
import { IScormLearnState } from 'src/app/interfaces/iscorm-learn-state';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';

const Scorm2004APICtor = (Scorm2004Ns as unknown as {
  Scorm2004API: new (settings?: { autocommit?: boolean; logLevel?: string }) => Scorm2004API;
}).Scorm2004API;

export interface ScormCallbacks {
  /** Fired on any CMI change with the FULL current state (never a partial). */
  onProgress: (state: IScormLearnState) => void;
  onTerminate: (finalState: IScormLearnState) => void;
}

// scorm-again Scorm2004API wrapper (the CMI runtime on window.API_1484_11):
// resume-seeds from IScormLearnState and maps CMI events to progress/completion.
export class ScormApi {
  readonly api: Scorm2004API;
  // cmi.exit is write-only per the SCORM 2004 spec — GetValue on it throws,
  // so we capture what the SCO writes here instead of reading it back later.
  private lastExit = '';

  constructor(initialState: IScormLearnState | null, private callbacks: ScormCallbacks) {
    this.api = new Scorm2004APICtor({ autocommit: false });
    this.lastExit = initialState?.exit ?? '';

    if (initialState) {
      const isCompleted =
        initialState.completion_status === PathwayConstants.SCORM_COMPLETION_STATUS_COMPLETED;

      this.api.loadFromJSON(
        {
          completion_status: initialState.completion_status,
          success_status: initialState.success_status,
          ...(initialState.score_raw !== undefined ? { score: { raw: initialState.score_raw } } : {}),
          ...(initialState.suspend_data ? { suspend_data: initialState.suspend_data } : {}),
          ...(!isCompleted && initialState.location ? { location: initialState.location } : {}),
        },
        'cmi'
      );
    }

    // scorm-again doesn't call this automatically after loadFromJSON — without it,
    // cmi.entry stays '' and Rise (and likely other authoring tools) treats the
    // course as "visited but not suspended", discarding within-section position.
    this.api.cmi.entry = this.api.determineEntryValue(
      initialState?.exit ?? '',
      !!initialState?.suspend_data
    );

    this.api.on('SetValue', (element: string, value: string) => {
      if (element === 'cmi.exit') this.lastExit = value;
    });

    // Persist on the SCO's own checkpoints, reading the FULL CMI each time so a
    // later write can't clobber an earlier `completed`.
    this.api.on('Commit', () => {
      this.callbacks.onProgress(this.getState());
    });
    this.api.on('Terminate', () => {
      this.callbacks.onTerminate(this.getState());
    });
  }

  /** Full current learner state read straight from the live CMI. */
  getState(): IScormLearnState {
    const cmi = this.api.cmi;
    const scoreRaw = cmi.score.raw;
    return {
      type: 'scorm',
      completion_status: cmi.completion_status as IScormLearnState['completion_status'],
      success_status: cmi.success_status as IScormLearnState['success_status'],
      score_raw: scoreRaw !== '' && scoreRaw !== undefined ? Number(scoreRaw) : undefined,
      suspend_data: cmi.suspend_data || undefined,
      location: cmi.location || undefined,
      exit: this.lastExit || undefined,
    };
  }
}
